import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldAlert, Mail } from 'lucide-react';
import { COMPANY } from '@/config/site';
import { API_URL } from '@/utils/api';

const CopyrightAndTakedown = () => {
  const [form, setForm] = useState({
    rights_holder_name: '',
    contact_email: '',
    contact_phone: '',
    infringing_work_description: '',
    project_url_or_reference: '',
    evidence_of_ownership: '',
    declaration_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit =
    form.rights_holder_name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim()) &&
    form.infringing_work_description.trim() &&
    form.evidence_of_ownership.trim() &&
    form.declaration_accepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please complete all required fields and accept the declaration.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/legal/copyright-report`, form);
      setSubmitted(true);
      toast.success('Report submitted — we will respond by email.');
    } catch (err) {
      console.error(err);
      toast.error('Could not submit the report. Please email ' + COMPANY.copyrightEmail + ' directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="copyright-and-takedown"
        defaultTitle="Copyright & Takedown Policy | House Plan Direct"
        defaultDescription="Report copyright infringement on House Plan Direct. Submit a takedown request or contact our copyright team."
      />
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-3">
          <ShieldAlert className="h-7 w-7 text-amber-600" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="copyright-title">Copyright &amp; Takedown</h1>
        </div>
        <p className="text-sm text-gray-500 mb-8">
          Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700">
          <p>
            {COMPANY.legalName} trading as {COMPANY.tradingAs} respects the intellectual-property rights of others.
            If you believe that material accessible on our website or produced through our tools infringes your copyright,
            or if a user has uploaded your work without permission, please submit a report using the form below.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-3">What to include</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>The rights-holder&apos;s name and contact details.</li>
            <li>A clear description of the copyrighted work you believe is being infringed.</li>
            <li>A URL, project reference or filename identifying the allegedly infringing material on our service.</li>
            <li>A brief explanation of why you believe the material is being used without authorisation.</li>
            <li>Evidence that you own the copyright or are authorised to act on the owner&apos;s behalf.</li>
            <li>A statement that the information in your notice is accurate.</li>
          </ul>

          <p>
            You can also email us directly at{' '}
            <a href={`mailto:${COMPANY.copyrightEmail}`} className="text-blue-600 hover:underline">{COMPANY.copyrightEmail}</a>.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-3">What happens next</h2>
          <p>
            We will acknowledge receipt within 5 working days, investigate the report, and where we reasonably believe
            infringement has occurred we will remove or disable access to the material. We may contact you for more
            information or share your report (excluding contact details where possible) with the uploading user so they
            can respond.
          </p>

          <p className="text-sm text-gray-500 italic">
            Submitting a false or misleading takedown notice may expose you to liability. If in doubt, obtain legal advice.
          </p>
        </div>

        {/* Report form */}
        <div className="mt-10 bg-white border border-gray-200 rounded-xl shadow-sm p-6 sm:p-8" data-testid="copyright-report-card">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Submit a copyright report</h2>

          {submitted ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-6 text-emerald-800" data-testid="copyright-submitted">
              <p className="font-semibold">Thank you — your report has been received.</p>
              <p className="text-sm mt-1">We aim to respond within 5 working days by email.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="rh-name">Rights holder name *</Label>
                <Input id="rh-name" required value={form.rights_holder_name}
                  onChange={(e) => setForm({ ...form, rights_holder_name: e.target.value })}
                  data-testid="cr-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rh-email">Contact email *</Label>
                  <Input id="rh-email" type="email" required value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    data-testid="cr-email" />
                </div>
                <div>
                  <Label htmlFor="rh-phone">Contact phone (optional)</Label>
                  <Input id="rh-phone" value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    data-testid="cr-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="rh-desc">Description of the allegedly infringing work *</Label>
                <Textarea id="rh-desc" required rows={4} value={form.infringing_work_description}
                  onChange={(e) => setForm({ ...form, infringing_work_description: e.target.value })}
                  data-testid="cr-desc" />
              </div>
              <div>
                <Label htmlFor="rh-url">URL or project reference (optional)</Label>
                <Input id="rh-url" value={form.project_url_or_reference}
                  onChange={(e) => setForm({ ...form, project_url_or_reference: e.target.value })}
                  placeholder="https://... or Order ref HPD202511..." data-testid="cr-url" />
              </div>
              <div>
                <Label htmlFor="rh-evidence">Evidence of ownership or authority *</Label>
                <Textarea id="rh-evidence" required rows={3} value={form.evidence_of_ownership}
                  onChange={(e) => setForm({ ...form, evidence_of_ownership: e.target.value })}
                  placeholder="e.g. Original filename, registration, publication URL, contract, or licence details."
                  data-testid="cr-evidence" />
              </div>

              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="rh-decl"
                  checked={form.declaration_accepted}
                  onCheckedChange={(v) => setForm({ ...form, declaration_accepted: !!v })}
                  data-testid="cr-declaration"
                />
                <label htmlFor="rh-decl" className="text-sm text-gray-700 cursor-pointer">
                  I declare, in good faith, that the information in this report is accurate and that I am the copyright
                  owner or authorised to act on the owner&apos;s behalf.
                </label>
              </div>

              <Button type="submit" disabled={!canSubmit || submitting}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="cr-submit">
                {submitting ? 'Submitting…' : 'Submit report'}
              </Button>
            </form>
          )}
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6 flex items-start gap-3">
          <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-900">
            Prefer email? Write to{' '}
            <a href={`mailto:${COMPANY.copyrightEmail}`} className="font-semibold underline">{COMPANY.copyrightEmail}</a>.
            You can also read our full <Link to="/legal/terms" className="underline">Terms &amp; Conditions</Link>.
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CopyrightAndTakedown;
